import { Link, useLocation } from 'react-router-dom'
import { Activity as ActivityIcon, BarChart3, Database, FolderTree, History, Home, ListTodo, Settings, ShieldCheck, Upload, Workflow } from 'lucide-react'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import type { Role } from '@/api/types'
import type { LucideIcon } from 'lucide-react'

export interface NavItem {
  to: string
  end?: boolean
  label: string
  icon: LucideIcon
  /** Roles that can see this link. `admin` always sees everything (handled below). */
  roles: Role[]
}

const ALL_ROLES: Role[] = ['admin', 'maintainer', 'radiologist', 'technician', 'developer', 'qa']

export const NAV: NavItem[] = [
  { to: '/', end: true, label: 'Home', icon: Home, roles: ALL_ROLES },
  // The one place new work appears: available, in progress, needs decision.
  // Radiologists claim anomaly reads and mask reviews from the same surface
  // technicians tag from, and maintainers triage flagged work here too.
  { to: '/work', label: 'Work', icon: ListTodo, roles: ['admin', 'maintainer', 'radiologist', 'technician', 'qa'] },
  // The one place you explore what you've done — terminal or not.
  { to: '/activity', label: 'Activity', icon: History, roles: ALL_ROLES },
  // Team throughput + the shape of the collection + the activity feed. The one
  // aggregated view; every other count in the app is derived client-side from the
  // caller's own task list, which can't answer either question.
  { to: '/dashboard', label: 'Dashboard', icon: BarChart3, roles: ['admin', 'maintainer'] },
  // Collections have exactly one job: explore and organize data pipelines.
  { to: '/collections', label: 'Collections', icon: FolderTree, roles: ['admin', 'maintainer'] },
  // The shape of the pipeline itself, as opposed to the data flowing through it.
  // Above Data deliberately: a maintainer picks a workflow when they START a study,
  // so it belongs beside Collections in the reading order, not filed under settings.
  { to: '/workflows', label: 'Workflows', icon: Workflow, roles: ['admin', 'maintainer'] },
  // Query, inspect, download. Export folded in here.
  { to: '/data', label: 'Data', icon: Database, roles: ['admin', 'maintainer', 'developer'] },
  { to: '/upload', label: 'Upload', icon: Upload, roles: ['admin', 'maintainer'] },
  { to: '/audit', label: 'Audit', icon: ShieldCheck, roles: ['admin', 'qa'] },
  { to: '/settings', label: 'Settings', icon: Settings, roles: ['admin', 'maintainer'] },
]

export function visibleNav(role: Role | undefined): NavItem[] {
  if (!role) return []
  return NAV.filter((item) => role === 'admin' || item.roles.includes(role))
}

/** Active when the path matches exactly (`end`) or is a prefix segment. */
export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

export function SidebarNav({ role, onNavigate }: { role: Role | undefined; onNavigate?: () => void }) {
  const { setOpenMobile, isMobile } = useSidebar()
  const { pathname } = useLocation()

  const handleClick = () => {
    if (isMobile) setOpenMobile(false)
    onNavigate?.()
  }

  return (
    <SidebarMenu>
      {visibleNav(role).map((item) => {
        const Icon = item.icon
        return (
          <SidebarMenuItem key={item.to}>
            {/* Plain Link, not NavLink: SidebarMenuButton needs `isActive` as a
                prop to set data-active, and NavLink only exposes it to a render
                prop it can't hand back up through asChild. */}
            <SidebarMenuButton asChild tooltip={item.label} isActive={isNavActive(item, pathname)}>
              <Link to={item.to} onClick={handleClick}>
                <Icon aria-hidden="true" />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )
      })}
    </SidebarMenu>
  )
}

export function getDefaultLanding(role: Role | undefined): string {
  switch (role) {
    case 'admin':
    case 'maintainer':
      return '/'
    case 'radiologist':
    case 'technician':
    case 'qa':
      return '/work'
    case 'developer':
      return '/data'
    default:
      return '/'
  }
}

export const NavBrandIcon = ActivityIcon
